/* eslint-disable react/no-unescaped-entities */
/* eslint-disable prefer-const */
/* eslint-disable no-unused-expressions */
/* eslint-disable no-restricted-syntax */
import React, { useRef, useEffect, useState } from 'react';
import i18n from '@utils/i18n';

import { Stack, Typography } from '@mui/material';
import useResponsive from '@hooks/useResponsive';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import env from '@constants/env';

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const Bg = `${env.s3BaseUrl}/landing-page/homepage/timeline-bg.png`;

const milestoneStyle = {
  fontSize: '18px',
  color: '#FFF',
  fontWeight: 500,
  minWidth: '400px',
  whiteSpace: 'pre-line',
  lineHeight: '100%',
  letterSpacing: '-0.9px',
  wordBreak: 'keep-all', // Prevent breaking words
  overflowWrap: 'break-word'
};

const SmoothScroll = () => {
  const timelineRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const bgRef = useRef(null);

  const t = i18n.getFixedT(null, null, 'pages.flagshop.timeline');

  const isMd = useResponsive('down', 1900);
  const [bgWidth, setBgWidth] = useState(1000);
  useEffect(() => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: timelineContainerRef.current,
        start: 'top top+=80',
        end: () =>
          `+=${
            timelineRef.current.scrollWidth -
            timelineRef.current.offsetWidth +
            200
          }`,
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        pinSpacing: true,
        markers: false, // Change to false in production
        id: 'hotplace-slider'
      }
    });

    // Add animations to the timeline
    timeline
      .to(timelineRef.current, {
        x: () =>
          -(timelineRef.current.scrollWidth - timelineRef.current.offsetWidth),
        ease: 'none'
      })
      .to(
        bgRef.current,
        {
          x: () =>
            -(
              timelineRef.current.scrollWidth -
              timelineRef.current.offsetWidth -
              200
            ), // Adjust this value based on your needs
          ease: 'none'
        },
        0
      )
      .to({}, { duration: 0.1 });

    return () => {
      timeline.kill();
      ScrollTrigger.getById('hotplace-slider')?.kill();
    };
  }, []);
  useEffect(() => {
    if (timelineRef.current) {
      setBgWidth(
        timelineRef.current.scrollWidth + timelineRef.current.offsetWidth + 200
      );
    }
  }, [timelineRef.current]);
  return (
    <Stack
      ref={timelineContainerRef}
      sx={{
        display: 'flex',
        py: '256px',
        alignItems: 'center',
        minHeight: '100vh',
        position: 'relative',
        '&::after': {
          // Add gradient overlay
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '20%', // Adjust width of gradient
          height: '100%',
          background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, #202020 100%)',
          pointerEvents: 'none', // Ensure it doesn't interfere with interactions
          zIndex: 1
        }
      }}
    >
      {/* Background with parallax effect */}
      <div
        ref={bgRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: bgWidth, // Increased width for better coverage
          height: '100%',
          backgroundImage: `url(${Bg})`,
          backgroundPosition: 'left center',
          backgroundRepeat: 'repeat-x', // Changed to repeat-x
          backgroundSize: '100% 100%', // Changed to maintain height and auto width
          zIndex: -1,
          transform: 'translateX(0)' // Initial position
        }}
      />

      {/* Horizontally moving timeline section */}
      <div
        ref={timelineRef}
        style={{
          width: '100%',
          position: 'relative',
          paddingLeft: isMd ? '10%' : '0'
        }}
      >
        <Stack
          sx={{
            width: { md: '100%', lg: '100%' },
            height: '100%',

            pl: { xs: '12.5%', lg: '320px' },
            display: { md: 'flex', xs: 'none' },
            flexDirection: 'row',
            position: 'relative',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: 10
          }}
        >
          
          <Stack
            textAlign="left"
            gap={3}
            sx={{ width: 'auto', position: 'sticky', left: 0, top: 0 }}
          >
            <Typography
              sx={{
                fontSize: '32px',
                color: '#FFF',
                fontFamily: 'Swear Display',
                fontWeight: 500,
                fontStyle: 'italic',
                lineHeight: '100%',
                letterSpacing: '-1.6px'
              }}
            >
              History
            </Typography>
            <Stack sx={{ gap: 5 }}>
              <Stack gap={2.5} sx={{ minWidth: '410px' }}>
                <Typography
                  fontWeight={700}
                  fontFamily="Pretendard"
                  sx={{
                    color: '#FFF',
                    fontSize: 48,
                    letterSpacing: '-2.4px',
                    lineHeight: '130%',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {t('title')}
                </Typography>
              </Stack>
              <Typography
                fontWeight={500}
                fontFamily="Pretendard"
                sx={{
                  color: '#FFF',
                  fontSize: 18,
                  letterSpacing: '-0.9px',
                  lineHeight: '100%',
                  whiteSpace: 'nowrap'
                }}
              >
                {t('subtitle')}
              </Typography>
            </Stack>
          </Stack>
          {/* timeline years */}
          <Stack
            sx={{
              flexDirection: 'row',
              pb: '100px',
              position: 'relative'
            }}
          >
            <Stack
              sx={{
                position: 'absolute',
                top: 50,
                left: 0,
                width: '100%',
                height: '1px',
                background:
                  'linear-gradient(90deg, rgba(255, 255, 255, 0.50) 0%, rgba(9, 11, 65, 0.50) 133.35%)'
              }}
            />
            {/* 2025 */}
            <Stack>
              {/* year */}
              <Stack
                sx={{
                  height: '50px',
                  minWidth: '400px',
                  justifyContent: 'center',
                  borderBottom: '0px solid #FFFFFF',
                  position: 'relative'
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  style={{
                    position: 'absolute',
                    left: '0px',
                    bottom: '-5px'
                  }}
                >
                  <circle cx="4.5" cy="4.5" r="4.5" fill="white" />
                </svg>
                <Typography
                  sx={{
                    fontSize: '24px',
                    color: '#FFF',
                    fontWeight: 500,
                    lineHeight: '100%',
                    letterSpacing: '-1.2px'
                  }}
                >
                  2025
                </Typography>
              </Stack>
              {/* milestones */}
              <Stack
                sx={{
                  mt: '19px',
                  gap: '10px',
                  minWidth: '400px'
                }}
              >
                <Typography sx={milestoneStyle}>
                  {t('2025.7.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2025.6.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2025.5.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2025.4.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2025.3.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2025.2.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2025.1.content')}
                </Typography>
              </Stack>
            </Stack>
            {/* 2024 */}
            <Stack>
              {/* year */}
              <Stack
                sx={{
                  height: '50px',
                  minWidth: '400px',
                  pl: '30px',
                  justifyContent: 'center',
                  position: 'relative',
                  borderBottom: '0px solid #FFFFFF'
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  style={{
                    position: 'absolute',
                    left: '30px',
                    bottom: '-5px'
                  }}
                >
                  <circle cx="4.5" cy="4.5" r="4.5" fill="white" />
                </svg>
                <Typography
                  sx={{
                    fontSize: '24px',
                    color: '#FFF',
                    fontWeight: 700,
                    lineHeight: '100%',
                    letterSpacing: '-1.2px'
                  }}
                >
                  2024
                </Typography>
              </Stack>
              {/* milestones */}
              <Stack
                sx={{
                  mt: '22px',
                  gap: '10px',
                  pl: '30px',
                  minWidth: '400px'
                }}
              >
                <Typography sx={milestoneStyle}>
                  {t('2024.10.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.9.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.8.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.7.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.6.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.5.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.4.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.3.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.2.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2024.1.content')}
                </Typography>
              </Stack>
            </Stack>
            {/* 2023 */}
            <Stack>
              {/* year */}
              <Stack
                sx={{
                  height: '50px',
                  minWidth: '400px',
                  pl: '30px',
                  borderBottom: '0px solid #FFFFFF',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  style={{
                    position: 'absolute',
                    left: '30px',
                    bottom: '-5px'
                  }}
                >
                  <circle cx="4.5" cy="4.5" r="4.5" fill="white" />
                </svg>
                <Typography
                  sx={{
                    fontSize: '24px',
                    color: '#FFF',
                    fontWeight: 700,
                    lineHeight: '100%',
                    letterSpacing: '-1.2px'
                  }}
                >
                  2023
                </Typography>
              </Stack>
              {/* milestones */}
              <Stack
                sx={{
                  mt: '22px',
                  gap: '10px',
                  pl: '30px',
                  minWidth: '400px'
                }}
              >
                <Typography sx={milestoneStyle}>
                  {t('2023.6.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2023.5.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2023.4.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2023.3.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2023.2.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2023.1.content')}
                </Typography>
              </Stack>
            </Stack>
            {/* 2022 */}
            <Stack>
              {/* year */}
              <Stack
                sx={{
                  height: '50px',
                  minWidth: '400px',
                  pl: '30px',
                  borderBottom: '0px solid #FFFFFF',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  style={{
                    position: 'absolute',
                    left: '30px',
                    bottom: '-5px'
                  }}
                >
                  <circle cx="4.5" cy="4.5" r="4.5" fill="white" />
                </svg>
                <Typography
                  sx={{
                    fontSize: '24px',
                    color: '#FFF',
                    fontWeight: 700,
                    lineHeight: '100%',
                    letterSpacing: '-1.2px'
                  }}
                >
                  2022
                </Typography>
              </Stack>
              {/* milestones */}
              <Stack
                sx={{
                  mt: '22px',
                  gap: '10px',
                  pl: '30px',
                  minWidth: '400px'
                }}
              >
                <Typography sx={milestoneStyle}>
                  {t('2022.3.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2022.2.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2022.1.content')}
                </Typography>
              </Stack>
            </Stack>
            {/* 2021 */}
            <Stack sx={{ width: 'auto' }}>
              {/* year */}
              <Stack
                sx={{
                  height: '50px',
                  minWidth: '400px',
                  width: '100%',
                  pl: '30px',
                  pr: '200px',
                  position: 'relative',
                  borderBottom: '0px solid #FFFFFF',
                  justifyContent: 'center'
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  style={{
                    position: 'absolute',
                    left: '30px',
                    bottom: '-5px'
                  }}
                >
                  <circle cx="4.5" cy="4.5" r="4.5" fill="white" />
                </svg>
                <Typography
                  sx={{
                    fontSize: '24px',
                    color: '#FFF',
                    fontWeight: 700,
                    lineHeight: '100%',
                    letterSpacing: '-1.2px'
                  }}
                >
                  2021
                </Typography>
              </Stack>
              {/* milestones */}
              <Stack
                sx={{
                  mt: '22px',
                  gap: '10px',
                  pl: '30px',
                  pr: '200px',
                  minWidth: '400px'
                }}
              >
                <Typography sx={milestoneStyle}>
                  {t('2021.8.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2021.7.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2021.6.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2021.5.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2021.4.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2021.3.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2021.2.content')}
                </Typography>
                <Typography sx={milestoneStyle}>
                  {t('2021.1.content')}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};

export default SmoothScroll;
